import React, { useEffect, useRef, useState } from 'react';
import {
  createStyles,
  makeStyles,
  Theme,
  Typography,
  Box,
} from '@material-ui/core';
import { useTranslation } from '../../../../../hooks/useTranslation';
interface StyleProps {
  isColumn: boolean;
}
const useStyles = makeStyles<Theme, StyleProps>(() =>
  createStyles({
    mainContainer: {
      margin: '0 0 10px',
      textAlign: 'center',
    },
    valuesAlignment: {
      width: 'auto !important',
    },
    rowContainer: {
      margin: '0.8rem auto',
      width: 'fit-content',
      '& > :first-child': {
        marginBottom: ({ isColumn }) => (isColumn ? '0.75rem' : '0'),
      },
    },
    row: {
      display: 'flex',
      flexDirection: ({ isColumn }) => 'column-reverse',
      justifyContent: 'flex-start',
      alignItems: 'center',
      flexWrap: 'wrap',
      '& > :first-child': {
        // width: set with js
        textAlign: ({ isColumn }) => (isColumn ? 'center' : 'right'),
        fontSize: '1.375rem',
        fontWeight: 'bold',
        lineHeight: '1.8rem',
      },
      '& > :nth-child(2)': {
        width: 'fit-content',
        wrap: 'no-wrap',
        textAlign: ({ isColumn }) => (isColumn ? 'center' : 'left'),
        paddingLeft: ({ isColumn }) => (isColumn ? '0' : '0.875rem'),
      },
    },
  }),
);
interface Props {
  totalOutages: number;
  customersAffected: number;
  lastupdated: string;
}
export const TerritorySummaryBlock = (props: Props) => {
  const { t } = useTranslation();
  const { totalOutages, customersAffected, lastupdated } = props;

  // Determine if layout adjustments are needed to accomodate displaying large outage numbers
  const isBigNumber = totalOutages > 9999 || customersAffected > 9999;
  const styleProps: StyleProps = { isColumn: isBigNumber };
  const classes = useStyles(styleProps);

  // Keep the outage count elements the same width - see design spec:
  //  - the count numbers are right justified and should line up on the y-axis
  //  - the descriptive text is left justified and should line up on the y-axis
  //  - overall row is centered based on the longest row: count number + descriptive text
  const [outageNumberBoxWidth, setNumberBoxWidth] = useState(0);
  const totalOutagesRef = useRef<HTMLElement>(null);
  const customersAffectedRef = useRef<HTMLElement>(null);
  if (totalOutagesRef.current && customersAffectedRef.current) {
    totalOutagesRef.current.style.width = isBigNumber
      ? '100%'
      : `${outageNumberBoxWidth}px`;
    customersAffectedRef.current.style.width = isBigNumber
      ? '100%'
      : `${outageNumberBoxWidth}px`;
  }
  useEffect(() => {
    if (
      isBigNumber ||
      !totalOutagesRef.current ||
      !customersAffectedRef.current
    ) {
      return;
    }
    // Reset the elements to establish the width based on the current content
    totalOutagesRef.current.style.width = 'fit-content';
    customersAffectedRef.current.style.width = 'fit-content';
    // Get both widths and use the larger of the two for both elements
    const a = totalOutagesRef.current?.offsetWidth;
    const b = customersAffectedRef.current?.offsetWidth;
    setNumberBoxWidth(Math.max(a, b));
  }, [totalOutages, customersAffected]);

  const totalPercentCount: any = t('TOTAL_PERCENT_CUSTOMERS_COUNT');
  const percentageAffected: string = (
    ((totalPercentCount - customersAffected) / totalPercentCount) *
    100
  ).toFixed(2);

  const getPercentageAffected = () => {
    if (
      (customersAffected >= 1 || customersAffected !== 0) &&
      Number(percentageAffected) > 99.99
    ) {
      return '99.99';
    }
    return percentageAffected;
  };

  return (
    <Box className={classes.mainContainer}>
      <Box className={classes.rowContainer}>
        <Box className={classes.row} data-testid="OMP-ActiveOutages">
          <Typography ref={totalOutagesRef} className={classes.valuesAlignment}>
            {totalOutages.toLocaleString()}
          </Typography>
          <Typography variant="body2">{t('TOTAL_ACTIVE_OUTAGES')}</Typography>
        </Box>
        <Box className={classes.row} data-testid="OMP-TotalCustomersAffected">
          <Typography
            ref={customersAffectedRef}
            className={classes.valuesAlignment}
          >
            {customersAffected.toLocaleString()}
          </Typography>
          <Typography variant="body2">
            {t('TOTAL_CUSTOMERS_AFFECTED')}
          </Typography>
        </Box>
        <Box className={classes.row}>
          <Typography className={classes.valuesAlignment}>
            {getPercentageAffected().toLocaleString()}%
          </Typography>
          <Typography variant="body2" style={{ marginTop: '0.75rem' }}>
            {t('TOTAL_PERCENT_CUSTOMERS_AFFECTED')}
          </Typography>
        </Box>
      </Box>
      <Box>
        <Typography
          style={{ fontSize: '0.75rem', fontWeight: 'bold' }}
          data-testid="OMP-LastUpdated"
        >
          {t('MAP_LAST_UPDATED')}: {lastupdated}
        </Typography>
        <Typography style={{ fontSize: '0.75rem' }}>
          {t('INFORMATION_UPDATES_EVERY')}
        </Typography>
      </Box>
    </Box>
  );
};

export default TerritorySummaryBlock;
